import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/uiComponents/components/ui/table";
import {
  deleteCategory,
  setCategories,
} from "@/store/redux/organization/categorySlice";
import { Button } from "@/uiComponents/components/ui/button";
import { IconPlus } from "@tabler/icons-react";

import {
  deleteCategoryApi,
  getCategoryApi,
} from "@/modules/organizationServices";

import { EllipsisVertical, SquarePen, Trash2 } from "lucide-react";
import AddCategory from "./AddCategory";
import UpdateCategory from "./UpdateCategory";
import { Category } from "@/types/organization";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/uiComponents/components/ui/dropdown-menu";
import { CategoryMessage } from "@/enums/errorMessages";
import { OrganizationType } from "@/types/apiResposne";
import { ErrorHelper } from "@/utils/errorHelper";
import DeleteModal from "../DeleteModal";
import { toastMessage } from "@/utils/toastHelper";
import { AddExpenseButton } from "@/utils/AddExpenseButton";

export default function Categories() {
  const dispatch = useDispatch();

  const categories = useSelector(
    (state: RootState) => state.category.categories
  );
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleDeleteCategory = async (id: number) => {
    try {
      await deleteCategoryApi(id);
      dispatch(deleteCategory(id));
      toastMessage("success", CategoryMessage.CATEGORY_DELETE);
      await getCategories();
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };
  const getCategories = async () => {
    try {
      const res = await getCategoryApi();
      const response = res.categories;
      if (response != undefined) {
        const categories: OrganizationType.CategoryResponse[] = Array.isArray(
          response
        )
          ? response
          : [response];
        dispatch(setCategories(categories));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  return (
    <div className="flex flex-1 flex-col max-w-screen md:ml-4 px-4  ">
      <div>
        <h2 className="text-2xl  font-semibold w-full">Categories</h2>

        <AddCategory onClose={() => setIsAddOpen(false)} isOpen={isAddOpen} />

        {selectedCategory && (
          <UpdateCategory
            onClose={() => setIsUpdateOpen(false)}
            isOpen={isUpdateOpen}
            category={selectedCategory}
          />
        )}
      </div>
      <AddExpenseButton />

      <div className="overflow-x-auto max-w-4xl mt-6 rounded-md ">
        <Table className="min-w-full bg-white border-1 ">
          <TableRow>
            <TableHead className="text-right" colSpan={4}>
              <Button
                className=" bg-[#191F29]  hover:bg-[#2A313D] active:bg-[#001533]  cursor-pointer  whitespace-nowrap mt-2 mb-2 text-white hover:text-white"
                onClick={() => setIsAddOpen(true)}
                variant="outline"
                size="sm"
              >
                <IconPlus />
                <span className="hidden lg:inline ml-1">Add Category</span>
              </Button>
            </TableHead>
          </TableRow>

          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell className="font-medium">{category.name}</TableCell>

                <TableCell colSpan={10} className="text-right pr-4">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-10 w-10 p-0 cursor-pointer hover:bg-gray-200 "
                      >
                        <span className="sr-only">Open menu</span>
                        <EllipsisVertical className=" text-gray-700 text-xl  " />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-32 rounded-md shadow-lg border bg-white"
                    >
                      <DropdownMenuItem
                        onClick={() => {
                          setIsUpdateOpen(true);
                          setSelectedCategory(category);
                        }}
                        className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                      >
                        <div className="flex gap-2">
                          <SquarePen size={16} /> Edit
                        </div>
                      </DropdownMenuItem>
                      <div className="h-px bg-gray-200 my-0.5 w-full" />
                      <DropdownMenuItem
                        onClick={() => {
                          setIsDeleteOpen(true);
                          setSelectedCategory(category);
                        }}
                        className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                      >
                        <div className="flex gap-2">
                          <Trash2 size={16} /> Delete
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {selectedCategory && (
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            item={selectedCategory}
            onConfirm={(item) => {
              handleDeleteCategory(item.id);
              setIsDeleteOpen(false);
              setSelectedCategory(null);
            }}
            itemLabel="category"
          />
        )}
      </div>
    </div>
  );
}
